import { useState, useCallback } from 'react'
import { useIntl } from 'react-intl'
import ShareIcon from '@mui/icons-material/Share'
import DownloadIcon from '@mui/icons-material/Download'
import CircularProgress from '@mui/material/CircularProgress'
import styles from './ViewerZoomControls.module.css'

interface ViewerZoomControlsProps {
  onZoomIn: () => void
  onZoomOut: () => void
  onShare?: () => void
  onExport?: () => Promise<void> | void
}

export function ViewerZoomControls({
  onZoomIn,
  onZoomOut,
  onShare,
  onExport,
}: ViewerZoomControlsProps) {
  const intl = useIntl()
  const [isExporting, setIsExporting] = useState(false)

  const handleExport = useCallback(async () => {
    if (!onExport || isExporting) return
    setIsExporting(true)
    try {
      await onExport()
    } catch (error) {
      console.error('Model export failed:', error)
    } finally {
      setIsExporting(false)
    }
  }, [onExport, isExporting])

  return (
    <div className={styles.zoomControls}>
      <button
        className={styles.zoomBtn}
        onClick={onZoomOut}
        aria-label='Zoom out'
        type='button'
      >
        −
      </button>
      <div className={styles.divider} />
      <button
        className={styles.zoomBtn}
        onClick={onZoomIn}
        aria-label='Zoom in'
        type='button'
      >
        +
      </button>

      {onExport && (
        <>
          <div className={styles.divider} />
          <div className={styles.btnWrapper}>
            <span className={styles.tooltip}>
              {intl.formatMessage({ id: 'viewer.export.tooltip' })}
            </span>
            <button
              className={styles.zoomBtn}
              onClick={handleExport}
              aria-label={intl.formatMessage({ id: 'viewer.export.tooltip' })}
              type='button'
              disabled={isExporting}
            >
              {isExporting ? (
                <CircularProgress size={18} color='inherit' />
              ) : (
                <DownloadIcon fontSize='small' />
              )}
            </button>
          </div>
        </>
      )}

      {onShare && (
        <>
          <div className={styles.divider} />
          <div className={styles.btnWrapper}>
            <span className={styles.tooltip}>
              {intl.formatMessage({ id: 'viewer.share.tooltip' })}
            </span>
            <button
              className={styles.zoomBtn}
              onClick={onShare}
              aria-label={intl.formatMessage({ id: 'viewer.share.tooltip' })}
              type='button'
            >
              <ShareIcon fontSize='small' />
            </button>
          </div>
        </>
      )}
    </div>
  )
}
